import React, {forwardRef, useContext} from 'react';
import cx from 'classnames';
import {IconWithPopover} from '../IconWithPopover';
import {
    FORMAT_ELEMENT_COMMAND,
    FORMAT_TEXT_COMMAND,
    INDENT_CONTENT_COMMAND,
    OUTDENT_CONTENT_COMMAND,
    REDO_COMMAND,
    UNDO_COMMAND
} from 'lexical';
import {
    IconRedo,
    IconUndo
} from '../../../../../features/Form/components/HTMLFormEditor/Editor/plugins/ToolbarPlugin/icons';
import {FontDropdown} from '../FontDropdown/FontDropdown';
import {
    DEFAULT_COLORS_MAP,
    defaultFontFamilyOption,
    defaultFontSizeOption,
    FONT_FAMILY_OPTIONS,
    FONT_SIZE_OPTIONS
} from '../../../constants';
import {FontDropDownMode} from '../FontDropdown/enums';
import ColorPicker from '../ColorPicker/ColorPicker';
import {noop, ToolbarIcons} from '@priority-software/priority-style-react';
import {EmojiPicker} from '../EmojiPicker';
import {INSERT_HORIZONTAL_RULE_COMMAND} from '@lexical/react/LexicalHorizontalRuleNode';
import {useLexicalComposerContext} from '@lexical/react/LexicalComposerContext';
import {Divider} from '../Divider';
import {EFormatIncreaseDecrease} from '../../../enums';
import {TollbarTextsContext} from './TollbarText.context';
import {isRTL} from '../../../../../Localization';

type Props = {
    canUndo?: boolean;
    canRedo?: boolean;
    toolbarId?: string;
    isBold?: boolean;
    isItalic?: boolean;
    isUnderline?: boolean;
    isStrikethrough?: boolean;
    formatBulletList?: () => void;
    formatNumberedList?: () => void;
    onFontColorSelect?: (color?: string) => void;
    showHistoryButtons?: boolean;
    showClipboardButtons?: boolean;
    showPrevColorButton?: boolean;
    style?: React.CSSProperties;
    children?: React.ReactNode;
    lastSelectedColor?: string;
    handleFontSizeChange: (mode: EFormatIncreaseDecrease) => void;
    handlePaste?: () => void;
    handleCopyAndCut?: (isCut?: boolean) => void;
    disabled?: boolean;
    emojiPickerPortalID: string;
};

export const BasicToolbar = forwardRef<HTMLDivElement, Props>((props, toolbarRef) => {
    const {
        canUndo = true,
        canRedo = true,
        showHistoryButtons,
        showPrevColorButton,
        showClipboardButtons,
        toolbarId,
        style,
        onFontColorSelect,
        isBold,
        isStrikethrough,
        isItalic,
        isUnderline,
        formatBulletList,
        formatNumberedList,
        lastSelectedColor,
        handleFontSizeChange,
        handlePaste = noop,
        handleCopyAndCut = noop,
        disabled,
        emojiPickerPortalID,
        children
    } = props;

    const rtl = isRTL();

    const [editor] = useLexicalComposerContext();
    const texts = useContext(TollbarTextsContext);

    const textAlignButtons = [
        <IconWithPopover
            key="align-left"
            type="icon"
            icon={ToolbarIcons.toolbarFormatAlignLeft}
            onClick={() => editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'left')}
            content={texts.align_l_btn_title}
            disabled={disabled}
        />,
        <IconWithPopover
            key="align-center"
            type="icon"
            icon={ToolbarIcons.toolbarFormatAlignCenter}
            onClick={() => editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'center')}
            content={texts.align_c_btn_title}
            disabled={disabled}
        />,
        <IconWithPopover
            key="align-right"
            type="icon"
            icon={ToolbarIcons.toolbarFormatAlignRight}
            onClick={() => editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'right')}
            content={texts.align_r_btn_title}
            disabled={disabled}
        />
    ].sort(() => (rtl ? -1 : 1));

    return (
        <div
            className={cx('priority-editor__toolbar')}
            ref={toolbarRef}
            style={style}
            id={toolbarId}
        >
            {showHistoryButtons ? (
                <>
                    <IconWithPopover type="children" content={texts.undo_btn_title}>
                        <button
                            className={cx('priority-editor__toolbar-item', {disabled: !canUndo})}
                            onClick={() => editor.dispatchCommand(UNDO_COMMAND, undefined)}
                            disabled={!canUndo}
                        >
                            <IconUndo className="format" />
                        </button>
                    </IconWithPopover>

                    <IconWithPopover type="children">
                        <button
                            className={cx('priority-editor__toolbar-item', {disabled: !canRedo})}
                            onClick={() => editor.dispatchCommand(REDO_COMMAND, undefined)}
                            disabled={!canRedo}
                        >
                            <IconRedo className="format" />
                        </button>
                    </IconWithPopover>

                    <Divider />
                </>
            ) : null}

            <FontDropdown
                options={FONT_FAMILY_OPTIONS}
                mode={FontDropDownMode.fontFamily}
                value={defaultFontFamilyOption}
                editor={editor}
                portalSelector={toolbarId}
                disabled={disabled}
            />

            <FontDropdown
                options={FONT_SIZE_OPTIONS}
                mode={FontDropDownMode.fontSize}
                value={defaultFontSizeOption}
                editor={editor}
                portalSelector={toolbarId}
                disabled={disabled}
            />

            <IconWithPopover
                type="icon"
                content={texts.reduce_text_size}
                icon={ToolbarIcons.toolbarTextDecrease}
                onClick={() => handleFontSizeChange(EFormatIncreaseDecrease.DECREASE)}
                disabled={disabled}
            />

            <IconWithPopover
                type="icon"
                content={texts.enlarge_text_size}
                icon={ToolbarIcons.toolbarTextIncrease}
                onClick={() => handleFontSizeChange(EFormatIncreaseDecrease.INCREASE)}
                disabled={disabled}
            />

            <Divider />

            <ColorPicker
                colorsPalette={DEFAULT_COLORS_MAP}
                buttonClassName="priority-editor__toolbar-item color-picker"
                buttonAriaLabel="Formatting text color"
                color={lastSelectedColor}
                onChange={onFontColorSelect}
                title="text color"
                disabled={disabled}
            />

            {showPrevColorButton ? (
                <IconWithPopover type="children" disabled={disabled}>
                    <button
                        className="priority-editor__toolbar-item"
                        onClick={() => onFontColorSelect()}
                        content={texts.txt_color_btn_title}
                    >
                        A
                    </button>
                </IconWithPopover>
            ) : null}

            {showClipboardButtons ? (
                <>
                    <IconWithPopover
                        type="icon"
                        content={texts.cut_btn_title}
                        icon={ToolbarIcons.toolbarCut}
                        onClick={() => handleCopyAndCut(true)}
                        disabled={disabled}
                    />
                    <IconWithPopover
                        type="icon"
                        content={texts.copy_btn_title}
                        icon={ToolbarIcons.toolbarCopy}
                        onClick={() => handleCopyAndCut()}
                        disabled={disabled}
                    />
                    <IconWithPopover
                        type="icon"
                        content={texts.paste_btn_title}
                        icon={ToolbarIcons.toolbarPaste}
                        onClick={handlePaste}
                        disabled={disabled}
                    />
                    <Divider />
                </>
            ) : null}

            <IconWithPopover
                type="icon"
                icon={ToolbarIcons.toolbarBold}
                onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold')}
                content={texts.bold_btn_title}
                componentClasses={cx({active: isBold})}
                disabled={disabled}
            />

            <IconWithPopover
                type="icon"
                icon={ToolbarIcons.toolbarItalic}
                componentClasses={cx({active: isItalic})}
                onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic')}
                content={texts.italic_btn_title}
                disabled={disabled}
            />

            <IconWithPopover
                type="icon"
                icon={ToolbarIcons.toolbarUnderline}
                componentClasses={cx({active: isUnderline})}
                onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'underline')}
                content={texts.underline_btn_title}
                disabled={disabled}
            />

            <IconWithPopover
                type="icon"
                content={texts.strikethrough_btn_title}
                icon={ToolbarIcons.toolbarFormatStrikethrough}
                componentClasses={cx({active: isStrikethrough})}
                onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'strikethrough')}
                disabled={disabled}
            />

            <Divider />

            {textAlignButtons.map(item => item)}

            <IconWithPopover
                type="icon"
                icon={
                    rtl
                        ? ToolbarIcons.toolbarFormatIndent_increase_rtl
                        : ToolbarIcons.toolbarFormatIndent_increase
                }
                onClick={() => editor.dispatchCommand(INDENT_CONTENT_COMMAND, undefined)}
                content={texts.increase_indent_btn_title}
                disabled={disabled}
            />

            <IconWithPopover
                type="icon"
                icon={
                    rtl
                        ? ToolbarIcons.toolbarFormatIndent_decrease_rtl
                        : ToolbarIcons.toolbarFormatIndent_decrease
                }
                onClick={() => editor.dispatchCommand(OUTDENT_CONTENT_COMMAND, undefined)}
                content={texts.decrease_indent_btn_title}
                disabled={disabled}
            />

            <Divider />

            <IconWithPopover
                type="icon"
                content={texts.bullets_btn_title}
                icon={
                    rtl
                        ? ToolbarIcons.toolbarFormatListBulletedRTL
                        : ToolbarIcons.toolbarFormatListBulleted
                }
                onClick={formatBulletList}
                disabled={disabled}
            />

            <IconWithPopover
                type="icon"
                content={texts.numbering_btn_title}
                icon={
                    rtl
                        ? ToolbarIcons.toolbarFormatListNumberedRTL
                        : ToolbarIcons.toolbarFormatListNumbered
                }
                onClick={formatNumberedList}
                disabled={disabled}
            />

            <Divider />

            <IconWithPopover
                type="icon"
                icon={ToolbarIcons.toolbarHorizontalRule}
                onClick={() => editor.dispatchCommand(INSERT_HORIZONTAL_RULE_COMMAND, undefined)}
                content={texts.hline_btn_title}
                disabled={disabled}
            />

            <EmojiPicker
                content={texts.emoji_btn_title}
                portalSelector={emojiPickerPortalID}
                disabled={disabled}
            />

            {children}
        </div>
    );
});
